import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Lightbulb, 
  ShoppingCart, 
  Phone, 
  Settings, 
  Shield, 
  DollarSign, 
  Clock, 
  Wrench, 
  Ruler, 
  Eye,
  Sparkles,
  Zap,
  CheckCircle
} from 'lucide-react';
import './AnimatedServicesSection.css';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function AnimatedServicesSection() {
  const [activeService, setActiveService] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const containerRef = useRef(null);

  const services = [
    {
      id: 1,
      title: "Expert Architectural Lighting Design",
      description: "You design your residence, we make it your home with the right light!",
      icon: Lightbulb,
      color: "#6366f1",
      gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
    },
    {
      id: 2,
      title: "One-stop Light Shop",
      description: "No more wandering around looking for parts, we offer you the whole.",
      icon: ShoppingCart,
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)"
    },
    {
      id: 3,
      title: "Outstanding Customer Support",
      description: "Our technically equipped team is at your service, even beyond office hours.",
      icon: Phone,
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
    },
    {
      id: 4,
      title: "Incredible Customization",
      description: "You imagine the experience you want to have, we will bring it alive.",
      icon: Settings,
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
    },
    {
      id: 5,
      title: "Competitive Long-term Warranty",
      description: "We protect our relationships with buyers until they are etched in stone.",
      icon: Shield,
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
    },
    {
      id: 6,
      title: "Best Value for Price",
      description: "We provide you exactly what we pledge, sometimes more but never less.",
      icon: DollarSign,
      color: "#06b6d4",
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
    },
    {
      id: 7,
      title: "Shortest Lead Time",
      description: "Your special requests are addressed immediately and carefully resolved.",
      icon: Clock,
      color: "#f97316",
      gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
    },
    {
      id: 8,
      title: "On-site Repair and Replacement",
      description: "We listen to your complaints to solve them instantaneously.",
      icon: Wrench,
      color: "#84cc16",
      gradient: "linear-gradient(135deg, #84cc16 0%, #65a30d 100%)"
    },
    {
      id: 9,
      title: "Coming soon! 3D Lighting Product Design",
      description: "Can't find your product in our catalogue? Let's draw it into existence!",
      icon: Ruler,
      color: "#ec4899",
      gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)"
    },
    {
      id: 10,
      title: "Coming soon! AR Lighting Design",
      description: "A 3D augmented reality recreation of your lighted space.",
      icon: Eye,
      color: "#a855f7",
      gradient: "linear-gradient(135deg, #a855f7 0%, #9333ea 100%)"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && containerRef.current) {
      // GSAP animations for scroll trigger
      gsap.fromTo(
        ".service-card",
        {
          y: 100,
          opacity: 0,
          scale: 0.8,
          rotation: -15
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          rotation: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: [0.68, -0.55, 0.27, 1.55],
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Floating animation for active service
      gsap.to(".active-service-card", {
        y: -20,
        duration: 2,
        ease: "power2.inOut",
        yoyo: true,
        repeat: -1
      });
    }
  }, [isVisible]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50, 
      scale: 0.8,
      rotateX: -15
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      y: -10,
      scale: 1.05,
      rotateY: 5,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    active: {
      scale: 1.1,
      rotateY: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: { 
      scale: 1, 
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: [0.68, -0.55, 0.27, 1.55]
      }
    },
    hover: {
      scale: 1.2,
      rotate: 360,
      transition: {
        duration: 0.5,
        ease: "easeInOut"
      }
    },
    active: {
      scale: 1.3,
      rotate: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const activeCardVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,
      y: -10,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section ref={sectionRef} className="services-section">
      <div className="services-container">
        <motion.div 
          className="services-header"
          initial={{ opacity: 0, y: -50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.68, -0.55, 0.27, 1.55] }}
          >
            Our Services
          </motion.h2>
          <motion.p 
            className="section-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.68, -0.55, 0.27, 1.55] }}
          >
            Comprehensive lighting solutions for every need
          </motion.p>
        </motion.div>
        
        <div ref={containerRef} className="services-layout">
          {/* Left side - Service cards */}
          <motion.div 
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
          >
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={service.id}
                  className={`service-card ${activeService === index ? 'active' : ''}`}
                  variants={cardVariants}
                  whileHover="hover"
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveService(index)}
                  style={{
                    '--service-color': service.color,
                    '--service-gradient': service.gradient
                  }}
                >
                  <motion.div 
                    className="service-icon-wrapper"
                    variants={iconVariants}
                  >
                    <div className="icon-background" />
                    <IconComponent 
                      size={32} 
                      className="service-icon"
                      style={{ color: service.color }}
                    />
                    <motion.div 
                      className="icon-glow"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={activeService === index ? { scale: 1, opacity: 1 } : {}}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </motion.div>
                  
                  <div className="service-content">
                    <motion.h3 
                      className="service-title"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      {service.title}
                    </motion.h3>
                    
                    <motion.div 
                      className="service-progress"
                      initial={{ scaleX: 0 }}
                      animate={activeService === index ? { scaleX: 1 } : {}}
                      transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                    />
                  </div>
                  
                  <motion.div 
                    className="service-indicator"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={activeService === index ? { scale: 1, rotate: 0 } : {}}
                    transition={{ duration: 0.5, ease: [0.68, -0.55, 0.27, 1.55] }}
                  />
                </motion.div>
              );
            })}
          </motion.div>
          
          {/* Right side - Active service display */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeService}
              className="active-service-display"
              variants={activeCardVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              whileHover="hover"
            >
              <div className="display-content">
                <motion.div 
                  className="active-icon-container"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.8, ease: [0.68, -0.55, 0.27, 1.55] }}
                >
                  <div className="active-icon">
                    <div className="icon-rings">
                      <motion.div 
                        className="ring ring-1"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      />
                      <motion.div 
                        className="ring ring-2"
                        animate={{ rotate: -360 }}
                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                      />
                      <motion.div 
                        className="ring ring-3"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                      />
                    </div>
                    {(() => {
                      const IconComponent = services[activeService].icon;
                      return <IconComponent size={64} className="icon-large" />;
                    })()}
                  </div>
                </motion.div>
                
                <motion.div 
                  className="active-service-info"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <motion.h2 
                    className="active-title"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.4, ease: [0.68, -0.55, 0.27, 1.55] }}
                  >
                    {services[activeService].title}
                  </motion.h2>
                  
                  <motion.p 
                    className="active-description"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                  >
                    {services[activeService].description}
                  </motion.p>
                  
                  <motion.div 
                    className="service-features"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    <motion.div 
                      className="feature-item"
                      whileHover={{ scale: 1.05, y: -5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Sparkles size={20} className="feature-icon" />
                      <span className="feature-text">Premium Quality</span>
                    </motion.div>
                    <motion.div 
                      className="feature-item"
                      whileHover={{ scale: 1.05, y: -5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Zap size={20} className="feature-icon" />
                      <span className="feature-text">Fast Delivery</span>
                    </motion.div>
                    <motion.div 
                      className="feature-item"
                      whileHover={{ scale: 1.05, y: -5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CheckCircle size={20} className="feature-icon" />
                      <span className="feature-text">Warranty Included</span>
                    </motion.div>
                  </motion.div>
                  
                  <motion.button 
                    className="cta-button"
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span>Learn More</span>
                    <motion.div 
                      className="button-glow"
                      animate={{ x: [-100, 100] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />
                  </motion.button>
                </motion.div>
              </div>
              
              <div className="display-background">
                <motion.div 
                  className="bg-particles"
                  animate={{ 
                    backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"]
                  }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        
        {/* Bottom navigation */}
        <motion.div 
          className="services-navigation"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="nav-dots">
            {services.map((_, index) => (
              <motion.button
                key={index}
                className={`nav-dot ${activeService === index ? 'active' : ''}`}
                onClick={() => setActiveService(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              />
            ))}
          </div>
          
          <div className="nav-controls">
            <motion.button 
              className="nav-btn prev-btn"
              onClick={() => setActiveService((prev) => (prev - 1 + services.length) % services.length)}
              whileHover={{ scale: 1.05, x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              ← Previous
            </motion.button>
            <motion.span 
              className="service-counter"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1 }}
            >
              {activeService + 1} / {services.length}
            </motion.span>
            <motion.button 
              className="nav-btn next-btn"
              onClick={() => setActiveService((prev) => (prev + 1) % services.length)}
              whileHover={{ scale: 1.05, x: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              Next →
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}